/**
 * Gator Oscillator
 *
 * Bill Williams' indicator showing convergence/divergence of the Alligator lines.
 * Upper histogram = |Jaw − Teeth|
 * Lower histogram = −|Teeth − Lips|
 *
 * Category: Advanced | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

function computeSMMA(values: number[], period: number): number[] {
  const result: number[] = [];
  let smma = NaN;
  for (let i = 0; i < values.length; i++) {
    if (i < period - 1) { result.push(NaN); continue; }
    if (i === period - 1) {
      let sum = 0; for (let j = 0; j < period; j++) sum += values[j];
      smma = sum / period;
    } else {
      smma = (smma * (period - 1) + values[i]) / period;
    }
    result.push(smma);
  }
  return result;
}

export const gatorIndicator: IndicatorDefinition = {
  id: "gator",
  name: "Gator Oscillator",
  shortName: "GATOR",
  category: "advanced",
  pane: "separate",
  params: [],
  plots: [
    { key: "upper", label: "Upper", color: "#4CAF50", style: "histogram", lineWidth: 1, priceScaleId: "gator", histogramBase: 0 },
    { key: "lower", label: "Lower", color: "#F44336", style: "histogram", lineWidth: 1, priceScaleId: "gator", histogramBase: 0 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const hl2 = candles.map(c => (c.high + c.low) / 2);
    const jaw = computeSMMA(hl2, 13);
    const teeth = computeSMMA(hl2, 8);
    const lips = computeSMMA(hl2, 5);

    const upper: { time: number; value: number; color?: string }[] = [];
    const lower: { time: number; value: number; color?: string }[] = [];

    for (let i = 0; i < candles.length; i++) {
      const ji = i - 8;
      const ti = i - 5;
      const li = i - 3;

      if (ji >= 0 && ti >= 0 && !isNaN(jaw[ji]) && !isNaN(teeth[ti])) {
        const val = Math.abs(jaw[ji] - teeth[ti]);
        const prevVal = upper.length > 0 ? upper[upper.length - 1].value : 0;
        upper.push({
          time: candles[i].time,
          value: val,
          color: val > prevVal ? "#4CAF50" : "#F44336",
        });
      }

      if (ti >= 0 && li >= 0 && !isNaN(teeth[ti]) && !isNaN(lips[li])) {
        const val = -Math.abs(teeth[ti] - lips[li]);
        const prevVal = lower.length > 0 ? lower[lower.length - 1].value : 0;
        lower.push({
          time: candles[i].time,
          value: val,
          color: val < prevVal ? "#F44336" : "#4CAF50",
        });
      }
    }

    return [{ key: "upper", data: upper }, { key: "lower", data: lower }];
  },
};
